import type { Collection, Challenge } from '@ai-platform/database';
import type { Course } from './courses';
import type { Prompt } from './prompts';

/** Значения/тип сортировки ленты */
export const FeedSortType = {
    All: 'all',
    Newest: 'newest',
    BestOfAll: 'best-of-all',
    BestOfMonth: 'best-of-month',
    BestOfWeek: 'best-of-week',
} as const;

export type FeedSortType = (typeof FeedSortType)[keyof typeof FeedSortType];

/**  Периоды для сортировки "Лучшее" */
export const FeedSortBestPeriodDays = {
    [FeedSortType.BestOfWeek]: 7,
    [FeedSortType.BestOfMonth]: 30,
    [FeedSortType.BestOfAll]: null,
} as const;

/**
 * Тип элемента в ленте - может быть курсом, челленджем, промптом или подборкой
 */
export const enum FeedItemType {
    Collection = 'collection',
    Course = 'course',
    Challenge = 'challenge',
    Prompt = 'prompt',
}

/**
 * Формула построения ленты
 */
export const enum FeedFormula {
    MVP = 'mvp',
    PromoResearch = 'promo-research',
}

/**
 * Элемент ленты с подборкой
 */
export type FeedCollectionItem = {
    type: FeedItemType.Collection;
    id: number;
    data: Collection;
};

/**
 * Элемент ленты с курсом
 */
export type FeedCourseItem = {
    type: FeedItemType.Course;
    id: number;
    data: Course;
};

/**
 * Элемент ленты с челленджем
 */
export type FeedChallengeItem = {
    type: FeedItemType.Challenge;
    id: number;
    data: Challenge;
};

/**
 * Элемент ленты с промптом
 */
export type FeedPromptItem = {
    type: FeedItemType.Prompt;
    id: number;
    data: Prompt;
};

/**
 * Объединенный тип элемента ленты
 */
export type FeedItem = FeedCourseItem | FeedChallengeItem | FeedPromptItem | FeedCollectionItem;

/**
 * Параметры для получения ленты
 */
export type FeedParams = {
    /** Абстрактный курсор для пагинации */
    cursor?: string;

    /** Размер страницы */
    limit?: number;

    /** Хэш для рандомной сортировки промптов */
    hash?: string;

    /** Фильтр по категориям */
    categories?: string[];

    /** Формула для ранжирования */
    formula?: string;

    /** Параметры для формулы */
    formulaParams?: string;
};

/**
 * Ответ API с лентой элементов
 */
export type FeedResponse = {
    /** Промпты */
    prompts: Prompt[];

    /** Подборки */
    collections: Collection[];

    /** Курсы */
    courses: Course[];

    /** Челленджи */
    challenges: Challenge[];

    /** Формула для ранжирования */
    formula?: string;

    /** Следующий абстрактный курсор */
    next_cursor: string | null;

    /** Есть ли еще курсы для показа */
    has_more_courses: boolean;

    /** Есть ли еще челленджи для показа */
    has_more_challenges: boolean;

    /** Есть ли еще промпты для показа */
    has_more_prompts: boolean;
};
